import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal } from "antd";

import AdminLeavesFilters from "../components/AdminLeavesFilters";
import AdminLeavesHeader from "../components/AdminLeavesHeader";
import AdminLeavesStats from "../components/AdminLeavesStats";
import AdminLeavesTable from "../components/AdminLeavesTable";
import ConfirmActionModal from "../components/modals/ConfirmActionModal";
import ExportLeavesModal from "../components/modals/ExportLeavesModal";
import RejectLeaveModal from "../components/modals/RejectLeaveModal";
import LeaveDetailsModal from "../../../../../../core/modals/leaveDetailModal";
import {
  fetchAdminLeaves,
  fetchAdminLeavesCompanies,
  fetchAdminLeavesEmployees,
  fetchAdminLeaveTypes,
  updateAdminLeaveStatus,
} from "../../../../../../core/data/redux/admin-leaves/thunks";
import {
  resetAdminLeavesFilters,
  setAdminLeavesActiveCompany,
  setAdminLeavesFilters,
} from "../../../../../../core/data/redux/admin-leaves/slice";
import {
  selectAdminLeaves,
  selectAdminLeavesActiveCompany,
  selectAdminLeavesCompanies,
  selectAdminLeavesEmployees,
  selectAdminLeavesError,
  selectAdminLeavesFilters,
  selectAdminLeavesLoading,
  selectAdminLeavesPagination,
  selectAdminLeavesStats,
  selectAdminLeaveTypes,
} from "../../../../../../core/data/redux/admin-leaves/selectors";
import { notify } from "../../../../../../services/toast.config";

import type {
  AdminLeavesFilters as AdminLeavesFiltersType,
  CompanyOption,
  LeaveHistory,
  LeaveStatus,
} from "../../../../../../core/data/redux/admin-leaves/types";
import type { AppDispatch } from "../../../../../../core/data/redux/store";

const LeaveAdminPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const leaves = useSelector(selectAdminLeaves);
  const stats = useSelector(selectAdminLeavesStats);
  const pagination = useSelector(selectAdminLeavesPagination);
  const filters = useSelector(selectAdminLeavesFilters);
  const activeCompany = useSelector(selectAdminLeavesActiveCompany);
  const companies = useSelector(selectAdminLeavesCompanies);
  const employees = useSelector(selectAdminLeavesEmployees);
  const leaveTypes = useSelector(selectAdminLeaveTypes);
  const loading = useSelector(selectAdminLeavesLoading);
  const error = useSelector(selectAdminLeavesError);

  const [selectedLeave, setSelectedLeave] = useState<LeaveHistory | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    leaveRequestId: number;
    status: LeaveStatus;
  } | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonModalText, setReasonModalText] = useState("");

  const filtersKey = useMemo(
    () =>
      `${activeCompany.companyId ?? ""}|${activeCompany.companyType ?? ""}|${JSON.stringify(filters)}`,
    [activeCompany.companyId, activeCompany.companyType, filters],
  );

  useEffect(() => {
    dispatch(fetchAdminLeavesCompanies());
  }, [dispatch]);

  useEffect(() => {
    if (!activeCompany.companyId && companies.length > 0) {
      const first = companies[0];
      dispatch(
        setAdminLeavesActiveCompany({
          companyId: first.companyId,
          companyType: first.companyType,
          companyName: first.label,
        }),
      );
    }
  }, [companies, activeCompany.companyId, dispatch]);

  useEffect(() => {
    if (activeCompany.companyId && activeCompany.companyType) {
      const companyId = activeCompany.companyId;
      const companyType = activeCompany.companyType;
      dispatch(fetchAdminLeavesEmployees({ companyId, companyType }));
      dispatch(fetchAdminLeaveTypes({ companyId, companyType }));
    }
  }, [activeCompany.companyId, activeCompany.companyType, dispatch]);

  useEffect(() => {
    if (activeCompany.companyId && activeCompany.companyType) {
      dispatch(
        fetchAdminLeaves({
          companyId: activeCompany.companyId,
          companyType: activeCompany.companyType,
          filters,
        }),
      );
    }
  }, [filtersKey, dispatch]);

  useEffect(() => {
    if (error) {
      notify.error(error);
    }
  }, [error]);

  const handleFilterChange = useCallback(
    (partial: Partial<AdminLeavesFiltersType>) => {
      dispatch(setAdminLeavesFilters(partial));
    },
    [dispatch],
  );

  const handleCompanyChange = useCallback(
    (company: CompanyOption) => {
      dispatch(
        setAdminLeavesActiveCompany({
          companyId: company.companyId,
          companyType: company.companyType,
          companyName: company.label,
        }),
      );
    },
    [dispatch],
  );

  const handleReset = useCallback(() => {
    dispatch(resetAdminLeavesFilters());
  }, [dispatch]);

  const handleView = useCallback((leave: LeaveHistory) => {
    setSelectedLeave(leave);
    setShowDetailsModal(true);
  }, []);

  const handleShowReason = useCallback((reason: string) => {
    setReasonModalText(reason);
    setReasonModalOpen(true);
  }, []);

  const handleAction = useCallback((leave: LeaveHistory, status: LeaveStatus) => {
    setPendingAction({ leaveRequestId: leave.id, status });
    if (status === "rejected") {
      setRejectionReason("");
      setShowRejectionModal(true);
    } else {
      setShowConfirmModal(true);
    }
  }, []);

  const closeActionModals = useCallback(() => {
    setShowConfirmModal(false);
    setShowRejectionModal(false);
    setPendingAction(null);
    setRejectionReason("");
  }, []);

  const handleConfirmAction = useCallback(async () => {
    if (!pendingAction) return;
    setActionSubmitting(true);
    try {
      const payload: {
        leaveRequestId: number;
        status: LeaveStatus;
        rejectionReason?: string;
      } = {
        leaveRequestId: pendingAction.leaveRequestId,
        status: pendingAction.status,
      };
      if (pendingAction.status === "rejected") {
        payload.rejectionReason = rejectionReason.trim();
      }
      await dispatch(updateAdminLeaveStatus(payload)).unwrap();
      notify.success(`Leave ${pendingAction.status} successfully`);
      if (activeCompany.companyId && activeCompany.companyType) {
        dispatch(
          fetchAdminLeaves({
            companyId: activeCompany.companyId,
            companyType: activeCompany.companyType,
            filters,
          }),
        );
      }
      closeActionModals();
    } catch (e: any) {
      notify.error(typeof e === "string" ? e : "Failed to update leave status");
    } finally {
      setActionSubmitting(false);
    }
  }, [
    pendingAction,
    rejectionReason,
    dispatch,
    activeCompany.companyId,
    activeCompany.companyType,
    filters,
    closeActionModals,
  ]);

  const selectedCompanyOption = useMemo(
    () =>
      activeCompany.companyId !== null && activeCompany.companyType !== null
        ? companies.find(
          (c) =>
            c.companyId === activeCompany.companyId &&
            c.companyType === activeCompany.companyType,
        ) || null
        : null,
    [activeCompany.companyId, activeCompany.companyType, companies],
  );

  const selectedEmployeeOption = useMemo(
    () =>
      filters.employeeId !== null
        ? employees.find((e) => Number(e.value) === filters.employeeId) || null
        : null,
    [filters.employeeId, employees],
  );

  const selectedLeaveTypeOption = useMemo(
    () =>
      filters.leaveTypeId !== null
        ? leaveTypes.find((t) => Number(t.value) === filters.leaveTypeId) || null
        : null,
    [filters.leaveTypeId, leaveTypes],
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <AdminLeavesHeader
            companies={companies}
            activeCompanyKey={selectedCompanyOption?.value || null}
            companiesLoading={companies.length === 0}
            onCompanyChange={handleCompanyChange}
            onExportClick={() => setShowExportModal(true)}
            exportDisabled={loading || !activeCompany.companyId}
          />

          <AdminLeavesStats stats={stats} loading={loading} />

          <AdminLeavesFilters
            filters={filters}
            employees={employees}
            leaveTypes={leaveTypes}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
          />

          <AdminLeavesTable
            leaves={leaves}
            loading={loading}
            pagination={pagination}
            filters={filters}
            onFilterChange={handleFilterChange}
            onView={handleView}
            onAction={handleAction}
            onShowReason={handleShowReason}
          />
        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By <span className="text-primary">ZYVO</span>
          </p>
        </div>
      </div>

      <LeaveDetailsModal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        leave={selectedLeave}
      />

      <ConfirmActionModal
        show={showConfirmModal}
        status={pendingAction?.status ?? null}
        submitting={actionSubmitting}
        onHide={closeActionModals}
        onConfirm={handleConfirmAction}
      />

      <RejectLeaveModal
        show={showRejectionModal}
        reason={rejectionReason}
        submitting={actionSubmitting}
        onChangeReason={setRejectionReason}
        onHide={closeActionModals}
        onConfirm={handleConfirmAction}
      />

      <ExportLeavesModal
        show={showExportModal}
        selectedCompany={selectedCompanyOption}
        selectedEmployee={selectedEmployeeOption}
        selectedLeaveType={selectedLeaveTypeOption}
        onHide={() => setShowExportModal(false)}
      />

      <Modal
        open={reasonModalOpen}
        onCancel={() => setReasonModalOpen(false)}
        title={
          <span className="d-flex align-items-center">
            <i className="ti ti-message-circle me-2 text-info" />
            Leave Reason
          </span>
        }
        footer={<Button onClick={() => setReasonModalOpen(false)}>Close</Button>}
        destroyOnClose
        width={520}
        zIndex={1001}
      >
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.6,
            color: "#334155",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            marginBottom: 0,
          }}
        >
          {reasonModalText}
        </p>
      </Modal>
    </>
  );
};

export default LeaveAdminPage;
